/**
 * Vonage Session Manager (Functional)
 *
 * Centralized session lifecycle management with:
 * - Retry logic with exponential backoff
 * - Connection health tracking to prevent cascading failures
 * - Proper error categorization for Sentry
 * - State cleanup coordination
 */

import { createLogger, logger as loggerService } from '@/services/logger/logger';
import type { VonageClient, VonageError as VonageSDKError } from '@vonage/client-sdk';

export type SessionErrorType =
  | 'session_timeout'
  | 'network_error'
  | 'auth_error'
  | 'connection_blocked'
  | 'unknown';

export type SessionOperation = 'create' | 'refresh';

/**
 * Every call into `createOrRefreshSession` must be tagged with a discriminated
 * trigger. This makes the scope of each call visible — a voice hook cannot
 * "just refresh" without inventing a reason, which surfaces scope violations
 * at code-review time.
 *
 * Variants:
 * - `initial-boot` — first session per app boot (or after an explicit reset).
 *   Fires `createSession(token, null)` at the SDK layer.
 * - `expired-token` / `transport-closed` / `ping-timeout` — the three
 *   SessionErrorReason values the SDK raises that require a resume. Carries
 *   the `previousSessionId` so `createSession(token, previousSessionId)`
 *   preserves the SDK's call registry.
 * - `ttl-heartbeat` — proactive resume fired by the provider every ~10min
 *   to stay ahead of Vonage's 15min server-side session TTL. WS keepalives
 *   extend the session while the tab is active, but backgrounded/throttled
 *   tabs can let it expire silently — the next serverCall then returns 401
 *   and the SDK surfaces it as a generic network failure. Uses resume
 *   semantics (carries `previousSessionId`) so an in-flight call isn't
 *   dropped. Expected cadence: 1 per ~10min per active tab.
 * - `manual-reset` — intentional fresh create outside initial boot. Used for
 *   the session-timeout retry loop, user-banner retry, and (pre-Phase-C)
 *   the call-layer reflexive refresh fallback. `reason` is a free-form tag
 *   that surfaces in telemetry so we can distinguish legitimate from bogus
 *   resets.
 */
export type SessionTrigger =
  | { type: 'initial-boot' }
  | { type: 'expired-token'; previousSessionId: string }
  | { type: 'transport-closed'; previousSessionId: string }
  | { type: 'ping-timeout'; previousSessionId: string }
  | { type: 'ttl-heartbeat'; previousSessionId: string }
  | { type: 'manual-reset'; reason: string };

const log = createLogger('VonageSessionManager', ['calls', 'vonage']);

const previousSessionIdOf = (trigger: SessionTrigger): string | undefined =>
  'previousSessionId' in trigger ? trigger.previousSessionId : undefined;

const operationOf = (trigger: SessionTrigger): SessionOperation =>
  'previousSessionId' in trigger ? 'refresh' : 'create';

const freshCreateFallbackReason = (trigger: SessionTrigger): string =>
  `${trigger.type}-auth-fallback`;

/**
 * Resolves when the browser reports network connectivity.
 *
 * A transport-closed `sessionError` typically fires within the first second
 * of a network drop, so the retry loop's exponential backoff (1s, 2s, 4s)
 * easily exhausts before the user's connection recovers — leaving the app
 * with no session and no self-heal path. Gating each attempt on
 * `navigator.onLine` keeps retries reserved for real session-layer failures
 * and lets coming-back-online drive recovery.
 */
const waitForOnline = (): Promise<void> => {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') {
    return Promise.resolve();
  }
  if (navigator.onLine) {
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    const handleOnline = () => {
      window.removeEventListener('online', handleOnline);
      resolve();
    };
    window.addEventListener('online', handleOnline);
  });
};

export interface SessionErrorContext {
  operation: SessionOperation;
  triggerType: SessionTrigger['type'];
  errorType: SessionErrorType;
  attempt: number;
  maxRetries: number;
  duration: number;
  hasSessionId: boolean;
  region?: string;
  errorMessage: string;
  errorCode?: string;
  vonageErrorType?: string;
}

/**
 * Circuit-breaker mode:
 * - `closed`    — normal operation, accumulating failures toward `maxFailures`.
 * - `open`      — breaker tripped, within block window. Calls short-circuit
 *                 via `isConnectionBlocked()` before reaching any network.
 * - `half-open` — block window elapsed. Exactly one probe attempt is allowed.
 *                 Success → closed (state reset). Failure → open with
 *                 exponential backoff (consecutiveTrips++).
 *
 * Transitions:
 *   closed  --(failures >= maxFailures)--> open        [ERROR log, onConnectionBlocked]
 *   open    --(nextRetryTime elapsed)---->  half-open  [INFO log]
 *   half-open --(success)----------------> closed      [INFO log, onConnectionRestored]
 *   half-open --(failure)----------------> open        [WARN log, no callback]
 */
export type ConnectionMode = 'closed' | 'open' | 'half-open';

export interface ConnectionHealthState {
  isBlocked: boolean;
  failures: number;
  lastFailureTime: number;
  nextRetryTime: number;
  mode: ConnectionMode;
  consecutiveTrips: number;
}

interface Logger {
  info: (message: string, meta?: Record<string, unknown>) => void;
  warn: (message: string, meta?: Record<string, unknown>) => void;
  error: (message: string, meta?: Record<string, unknown>) => void;
}

interface SessionManagerOptions {
  maxRetries?: number;
  maxFailures?: number;
  blockDuration?: number;
  maxBlockDuration?: number;
  logger?: Logger;
  onError?: (context: SessionErrorContext) => void;
  onConnectionBlocked?: () => void;
  onConnectionRestored?: () => void;
}

export interface VonageSessionManager {
  createOrRefreshSession: (
    client: VonageClient,
    getToken: () => Promise<string>,
    options: {
      trigger: SessionTrigger;
      region?: string;
      userId?: string;
      businessId?: string;
    },
  ) => Promise<string | undefined>;
  getConnectionState: () => ConnectionHealthState;
  resetConnection: () => void;
}

export const createVonageSessionManager = (
  options: SessionManagerOptions = {},
): VonageSessionManager => {
  const maxRetries = options.maxRetries ?? 3;
  const maxFailures = options.maxFailures ?? 5;
  const blockDuration = options.blockDuration ?? 60000;
  const maxBlockDuration = options.maxBlockDuration ?? 600000;

  const freshConnectionHealth = (): ConnectionHealthState => ({
    isBlocked: false,
    failures: 0,
    lastFailureTime: 0,
    nextRetryTime: 0,
    mode: 'closed',
    consecutiveTrips: 0,
  });

  // Exponential backoff on repeated trips. 1st trip uses `blockDuration`
  // (default 60s); each subsequent trip doubles it, capped at `maxBlockDuration`
  // (default 10 min). Prevents the pre-fix behavior where persistent backend
  // rejection produced one ERROR log every 60s forever.
  const getBackoffDuration = (consecutiveTrips: number): number => {
    const exponent = Math.max(consecutiveTrips - 1, 0);
    return Math.min(blockDuration * 2 ** exponent, maxBlockDuration);
  };

  let connectionHealth: ConnectionHealthState = freshConnectionHealth();

  let inFlightSession: Promise<string | undefined> | null = null;

  const isVonageError = (error: unknown): error is VonageSDKError => {
    return typeof error === 'object' && error !== null && 'code' in error && 'type' in error;
  };

  const isNetworkError = (error: unknown): boolean => {
    if (!(error instanceof Error)) {
      return false;
    }

    const message = error.message.toLowerCase();
    return (
      // Network fetch errors (TypeError with various formats)
      (error instanceof TypeError && message.includes('failed to fetch')) ||
      (error instanceof TypeError && message.includes('network request failed')) ||
      (error instanceof TypeError && message.includes('network error')) ||
      // Generic network-related errors
      message.includes('network') ||
      message.includes('timeout') ||
      message.includes('connection')
    );
  };

  const getErrorMessage = (error: unknown): string =>
    error instanceof Error ? error.message : String(error);

  const getRetryDelay = (attempt: number): number => 1000 * 2 ** (attempt - 1);

  const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

  const categorizeError = (error: unknown): SessionErrorType => {
    if (isVonageError(error)) {
      if (error.code === 'session:error:timeout') {
        return 'session_timeout';
      }
      if (error.code?.includes('auth') || error.code?.includes('token')) {
        return 'auth_error';
      }
    }

    const message = getErrorMessage(error).toLowerCase();
    if (
      message.includes('unauthorized') ||
      message.includes('authorization') ||
      message.includes('expired-token') ||
      message.includes('expired token') ||
      message.includes('auth') ||
      message.includes('token')
    ) {
      return 'auth_error';
    }

    if (isNetworkError(error)) {
      return 'network_error';
    }

    return 'unknown';
  };

  const shouldRetryError = (errorType: SessionErrorType, attempt: number): boolean =>
    attempt < maxRetries && (errorType === 'network_error' || errorType === 'session_timeout');

  const generateSentryFingerprint = (context: SessionErrorContext): string[] => [
    'vonage-session',
    context.operation,
    context.errorType,
  ];

  const reportError = (
    context: SessionErrorContext,
    sessionOptions: { userId?: string; businessId?: string; region?: string },
  ): void => {
    options.onError?.(context);

    const fingerprint = generateSentryFingerprint(context);

    loggerService.captureException(
      new Error(`Vonage ${context.operation} failed: ${context.errorType}`),
      {
        fingerprint,
        tags: {
          component: `vonage_session_${context.operation}`,
          error_type: context.errorType,
          operation: context.operation,
          region: context.region ?? 'unknown',
          has_session_id: String(context.hasSessionId),
          connection_blocked: String(connectionHealth.isBlocked),
        },
        level: context.errorType === 'connection_blocked' ? 'warning' : 'error',
        context: {
          attempt: context.attempt,
          maxRetries: context.maxRetries,
          duration: context.duration,
          errorMessage: context.errorMessage,
          errorCode: context.errorCode,
          vonageErrorType: context.vonageErrorType,
          userId: sessionOptions.userId,
          businessId: sessionOptions.businessId,
          failureCount: connectionHealth.failures,
        },
      },
    );
  };

  /**
   * Pure read: is the breaker currently blocking session operations?
   * Returns true only when mode is `open` AND the retry timer hasn't elapsed.
   * Does NOT transition the state machine — call `allowProbeAttemptIfReady()`
   * explicitly before attempting a network operation so the open→half-open
   * transition is tied to an actual probe, not to a speculative read.
   */
  const isConnectionBlocked = (): boolean => {
    if (connectionHealth.mode !== 'open') {
      return false;
    }
    return Date.now() < connectionHealth.nextRetryTime;
  };

  /**
   * If the breaker is `open` and the retry window has elapsed, transition to
   * `half-open` so the next session operation is treated as the single allowed
   * probe. No-op in any other state. Must be called exactly once per probe
   * attempt (i.e. from `doSession` just before invoking `createSession`).
   *
   * Intentionally does NOT clear `failures` or `consecutiveTrips` — those only
   * reset on a successful probe via `recordSuccess`. If the probe fails,
   * `recordFailure` re-opens with exponential backoff using `consecutiveTrips`.
   */
  const allowProbeAttemptIfReady = (): void => {
    if (connectionHealth.mode !== 'open') {
      return;
    }
    if (Date.now() < connectionHealth.nextRetryTime) {
      return;
    }
    connectionHealth.mode = 'half-open';
    connectionHealth.isBlocked = false;
    const message = '[VonageSessionManager] Block expired, allowing half-open probe';
    options.logger?.info(message, {
      consecutiveTrips: connectionHealth.consecutiveTrips,
      failures: connectionHealth.failures,
    }) ?? console.log(message);
  };

  const recordSuccess = (): void => {
    const wasUnhealthy = connectionHealth.mode !== 'closed' || connectionHealth.failures > 0;
    if (wasUnhealthy) {
      const message = '[VonageSessionManager] Connection restored after success';
      options.logger?.info(message) ?? console.log(message);
      options.onConnectionRestored?.();
    }

    connectionHealth = freshConnectionHealth();
  };

  const recordFailure = (): void => {
    const now = Date.now();
    connectionHealth.lastFailureTime = now;

    // Half-open probe failed → re-open with exponential backoff.
    // Do NOT increment `failures` or re-fire the ERROR-level log/callback:
    // the first trip already signaled the transition; further climbing of
    // the counter while the backend is persistently rejecting this session
    // is exactly the runaway noise the pre-fix implementation produced.
    if (connectionHealth.mode === 'half-open') {
      connectionHealth.consecutiveTrips += 1;
      connectionHealth.mode = 'open';
      connectionHealth.isBlocked = true;
      const backoffMs = getBackoffDuration(connectionHealth.consecutiveTrips);
      connectionHealth.nextRetryTime = now + backoffMs;

      const message = '[VonageSessionManager] Half-open probe failed, re-blocking with backoff';
      options.logger?.warn(message, {
        consecutiveTrips: connectionHealth.consecutiveTrips,
        failures: connectionHealth.failures,
        backoffMs,
      }) ?? console.warn(message);
      return;
    }

    // Normal accumulation while closed. Only the FIRST trip (closed → open)
    // emits an ERROR log and fires onConnectionBlocked. This keeps one trip
    // = one Sentry error group, rather than N-per-retry-cycle.
    connectionHealth.failures += 1;

    if (connectionHealth.failures >= maxFailures && connectionHealth.mode === 'closed') {
      connectionHealth.consecutiveTrips = 1;
      connectionHealth.mode = 'open';
      connectionHealth.isBlocked = true;
      connectionHealth.nextRetryTime = now + blockDuration;

      const message = `[VonageSessionManager] Connection blocked after ${connectionHealth.failures} failures`;
      options.logger?.error(message, { failures: connectionHealth.failures }) ??
        console.error(message);

      options.onConnectionBlocked?.();
    }
  };

  return {
    createOrRefreshSession: (
      client: VonageClient,
      getToken: () => Promise<string>,
      sessionOptions: {
        trigger: SessionTrigger;
        region?: string;
        userId?: string;
        businessId?: string;
      },
    ) => {
      if (inFlightSession) {
        const message = '[VonageSessionManager] Session operation already in flight, deduplicating';
        options.logger?.info(message) ?? console.log(message);
        return inFlightSession;
      }

      // The input trigger is the starting point. The retry loop may re-label
      // a session_timeout retry as `manual-reset` since it intentionally drops
      // the previousSessionId for a fresh create.
      let effectiveTrigger: SessionTrigger = sessionOptions.trigger;

      const doSession = async (): Promise<string | undefined> => {
        if (isConnectionBlocked()) {
          const context: SessionErrorContext = {
            operation: operationOf(effectiveTrigger),
            triggerType: effectiveTrigger.type,
            errorType: 'connection_blocked',
            attempt: 0,
            maxRetries: 0,
            duration: 0,
            hasSessionId: !!previousSessionIdOf(effectiveTrigger),
            region: sessionOptions.region,
            errorMessage: 'Connection blocked - too many recent failures',
          };

          reportError(context, sessionOptions);
          throw new Error('Session creation blocked due to too many failures');
        }

        // Breaker is not actively blocking. If we're in `open` but the retry
        // window has elapsed, promote to `half-open` now so this attempt is
        // treated as the single allowed probe and `recordFailure` can key
        // exponential backoff off `consecutiveTrips` on a probe miss.
        allowProbeAttemptIfReady();

        const startTime = Date.now();
        let attempt = 0;
        let currentSessionId = previousSessionIdOf(effectiveTrigger);

        while (attempt <= maxRetries) {
          // Dev-only regression sentinel: any createSession(token, null) outside
          // `initial-boot` or `manual-reset` means a refresh path has lost its
          // previousSessionId (R2/R3 from the spec) and would re-create the
          // session from scratch instead of resuming.
          if (
            import.meta.env.DEV &&
            !currentSessionId &&
            effectiveTrigger.type !== 'initial-boot' &&
            effectiveTrigger.type !== 'manual-reset'
          ) {
            console.warn(
              `[VonageSessionManager] unexpected create without previousSessionId outside initial-boot/manual-reset (trigger: ${effectiveTrigger.type})`,
              new Error().stack,
            );
          }

          // Don't burn a retry on a known-offline transport. The retry ladder
          // (1s/2s/4s) usually exhausts before a real outage ends, which left
          // the app stuck with no session even after the user came back online
          // — no listener re-triggered recovery. Parking the attempt here
          // means connectivity itself drives the next try.
          if (typeof navigator !== 'undefined' && !navigator.onLine) {
            const offlineMeta = {
              operation: operationOf(effectiveTrigger),
              triggerType: effectiveTrigger.type,
              attempt,
            };
            const offlineMessage =
              '[VonageSessionManager] navigator offline, waiting for online event before session attempt';
            options.logger?.info(offlineMessage, offlineMeta) ??
              console.log(offlineMessage, offlineMeta);
            await waitForOnline();
          }

          try {
            const token = await getToken();

            const sessionCreateStart = Date.now();

            // `refreshSession` rotates the JWT in place but requires an active
            // session, so it's reserved for the proactive `ttl-heartbeat` path.
            // Reactive triggers use `createSession` because the session may
            // already be dead by the time we get here.
            let newSessionId: string;
            let usedRefreshSession = false;

            if (effectiveTrigger.type === 'ttl-heartbeat' && currentSessionId) {
              try {
                await client.refreshSession(token);
                newSessionId = currentSessionId;
                usedRefreshSession = true;
              } catch (refreshError) {
                const refreshFailedMeta = {
                  triggerType: effectiveTrigger.type,
                  previousSessionId: currentSessionId,
                  errorMessage: getErrorMessage(refreshError),
                  errorCode: isVonageError(refreshError)
                    ? (refreshError.code ?? undefined)
                    : undefined,
                  vonageErrorType: isVonageError(refreshError) ? refreshError.type : undefined,
                  timestamp: Date.now(),
                };

                options.logger?.warn(
                  '[VonageSessionManager] refreshSession failed, falling back to createSession',
                  refreshFailedMeta,
                );
                log.warn(
                  'Session refresh failed, falling back to createSession',
                  refreshFailedMeta,
                );
                newSessionId = await client.createSession(token, currentSessionId);
              }
            } else {
              newSessionId = await client.createSession(token, currentSessionId);
            }

            const sessionCreateDuration = Date.now() - sessionCreateStart;

            const operation = operationOf(effectiveTrigger);
            const meta = {
              operation,
              triggerType: effectiveTrigger.type,
              sessionCreateDuration,
              totalDuration: Date.now() - startTime,
              attempt,
              usedRefreshSession,
            };
            const message = `[VonageSessionManager] Session ${operation} succeeded`;
            options.logger?.info(message, meta);

            // `initial-boot` and `ttl-heartbeat` are expected, scheduled
            // triggers — INFO. Everything else (manual-reset, SDK-error
            // resumes) is WARN so the regression dashboards stay readable
            // without having to filter out the heartbeat.
            if (usedRefreshSession) {
              log.info('Session refreshed', {
                triggerType: effectiveTrigger.type,
                sessionId: currentSessionId,
                sessionCreateDuration,
                timestamp: Date.now(),
              });
            } else {
              const isExpectedTrigger =
                effectiveTrigger.type === 'initial-boot' ||
                effectiveTrigger.type === 'ttl-heartbeat';
              const sessionCreateContext = {
                triggerType: effectiveTrigger.type,
                triggerReason:
                  effectiveTrigger.type === 'manual-reset' ? effectiveTrigger.reason : null,
                hasPreviousSessionId: !!currentSessionId,
                previousSessionId: currentSessionId ?? null,
                newSessionId: newSessionId ?? null,
                sessionId: newSessionId ?? null,
                timestamp: Date.now(),
              };
              if (isExpectedTrigger) {
                log.info('Session created', sessionCreateContext);
              } else {
                log.warn('Session created', sessionCreateContext);
              }
            }

            recordSuccess();

            return newSessionId;
          } catch (error: unknown) {
            const attemptDuration = Date.now() - startTime;
            const errorType = categorizeError(error);
            const shouldRetry = shouldRetryError(errorType, attempt);

            const operation = operationOf(effectiveTrigger);
            // Auth error on a resume attempt with a previousSessionId means
            // the session is gone server-side. Strip the prevId and retry as
            // a fresh create so signaling recovers instead of leaving the
            // user stuck for the rest of the call.
            const shouldFallbackToFreshCreate =
              errorType === 'auth_error' &&
              !!currentSessionId &&
              effectiveTrigger.type !== 'manual-reset';

            if (shouldFallbackToFreshCreate) {
              const previousSessionId = currentSessionId;
              const fallbackReason = freshCreateFallbackReason(effectiveTrigger);

              options.logger?.warn(
                '[VonageSessionManager] Refresh auth failed, creating fresh session',
                {
                  operation,
                  triggerType: effectiveTrigger.type,
                  fallbackReason,
                  attempt,
                  attemptDuration,
                },
              );
              log.warn('Refresh auth failed, creating fresh session', {
                triggerType: effectiveTrigger.type,
                fallbackReason,
                previousSessionId,
                timestamp: Date.now(),
              });

              effectiveTrigger = { type: 'manual-reset', reason: fallbackReason };
              currentSessionId = undefined;
              continue;
            }

            const context: SessionErrorContext = {
              operation,
              triggerType: effectiveTrigger.type,
              errorType,
              attempt,
              maxRetries,
              duration: attemptDuration,
              hasSessionId: !!currentSessionId,
              region: sessionOptions.region,
              errorMessage: getErrorMessage(error),
              errorCode: isVonageError(error) ? (error.code ?? undefined) : undefined,
              vonageErrorType: isVonageError(error) ? error.type : undefined,
            };

            // last attempt or non-retryable error
            if (!shouldRetry || attempt >= maxRetries) {
              recordFailure();
              reportError(context, sessionOptions);

              const errorMeta = { ...context, connectionHealthState: connectionHealth };
              const errorMessage = `[VonageSessionManager] Session ${operation} failed`;
              options.logger?.error(errorMessage, errorMeta);

              throw error;
            }

            // retry logic
            attempt++;
            const delayMs = getRetryDelay(attempt);

            const warnMeta = {
              operation,
              triggerType: effectiveTrigger.type,
              errorType,
              delayMs,
              attemptDuration,
            };
            const warnMessage = `[VonageSessionManager] ${errorType} detected, retrying... (${attempt}/${maxRetries})`;
            options.logger?.warn(warnMessage, warnMeta) ?? console.warn(warnMessage, warnMeta);

            // Session-timeout retries intentionally drop the previousSessionId
            // for a fresh create. Re-label the trigger so telemetry, the dev
            // assertion, and the Sentry fingerprint all agree this is a
            // legitimate manual-reset rather than a bogus create.
            if (errorType === 'session_timeout') {
              effectiveTrigger = { type: 'manual-reset', reason: 'session-timeout-retry' };
              currentSessionId = undefined;
            }

            await delay(delayMs);
          }
        }

        // Should never reach here
        throw new Error('Max retries exceeded');
      };

      inFlightSession = doSession().finally(() => {
        inFlightSession = null;
      });

      return inFlightSession;
    },

    getConnectionState: () => ({ ...connectionHealth }),

    resetConnection: () => {
      const message = '[VonageSessionManager] Connection manually reset';
      options.logger?.info(message) ?? console.log(message);
      connectionHealth = freshConnectionHealth();
      options.onConnectionRestored?.();
    },
  };
};
